import React, { useState, useEffect, useContext } from 'react';
import './effortUpload.scss';
import DataGridComponent from '../../../Components/DataGrid/dataGrid';
import ButtonComponent from '../../../Components/Button/button';
import { EFFORT_UPLOAD, FILE_UPLOAD, LEAVE_UPLOAD } from '../../../Constants/strings';
import FileUploadPopUp from './fileUploadPopUp';
import { AuthContext } from '../../../Contexts/AuthContext';
import dayjs from 'dayjs';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import UseAxios from '../../../Api/http/http';
import { STATUS_CODES } from '../../../Api/resources/statusCodes';
import { MODULE_NAMES } from '../../../Constants/modules';
import { effortUploadListForFileUplaod } from '../../../Api/request/effortUploadListForFileUpload';

const FileUpload = () => {
  const [rows, setRows] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // New state for triggering refresh

  const { userProfile } = useContext(AuthContext);
  const [isMobileView, setIsMobileView] = useState(false);
  const permission = userProfile?.permissions
    ? userProfile.permissions[MODULE_NAMES.CONFIGURATION_EFFORT_UPLOAD] || []
    : [];

  const { snackbarOpen } = useSnackbar();

  const checkAccess = (action) => {
    return permission.includes(action);
  };

  const fetchEffortHistory = () => {
    let apiCall = null;
    apiCall = effortUploadListForFileUplaod();

    UseAxios(apiCall)
      .then((response) => {
        if (response.data?.response?.data?.statusCode === STATUS_CODES.NO_DATA) {
          setRows([]);
        }
        const effortHistory = response.data.schedulerHistory.map(row => {
          if (row.status === 'Failed') {
            return { ...row, startDate: null, endDate: null };
          }
          return row;
        });
        setRows(effortHistory);
      })
      .catch((error) => {
        snackbarOpen(EFFORT_UPLOAD.OOPS_TRY_AGAIN, 'warning');
        setRows([]);
        console.error(error);
      });
  };

  useEffect(() => {
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);

  const checkMobileView = () => {
    setIsMobileView(window.innerWidth < 1024);
  };

  // This effect will run on mount and whenever refreshTrigger changes
  useEffect(() => {
    fetchEffortHistory();
  }, [refreshTrigger]);

  const handleUploadComplete = () => {
    setRefreshTrigger(prev => prev + 1); // Increment to trigger refresh
  };

  const filteredRows = rows || [];

  const getColumnWidth = (mobileView, mobileWidth, desktopWidth) => {
    return mobileView ? mobileWidth : desktopWidth;
  };

  const getColumnFlex = (mobileView, mobileFlex, desktopFlex) => {
    return mobileView ? mobileFlex : desktopFlex;
  };

  const columns = [
    {
      field: EFFORT_UPLOAD.UPDATED_START_DATE,
      headerName: EFFORT_UPLOAD.UPLOADED_START_TIME,
      flex: getColumnFlex(isMobileView, undefined, 0.22),
      width: getColumnWidth(isMobileView, 200, undefined),
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (params) => dayjs(params).format('DD/MM/YYYY h:mm:ss A'),
      showDateFilter: true,
      showTextFilter: false,
    },
    {
      field: EFFORT_UPLOAD.UPDATED_END_DATE,
      headerName: EFFORT_UPLOAD.UPLOADED_END_TIME,
      flex: getColumnFlex(isMobileView, undefined, 0.2),
      width: getColumnWidth(isMobileView, 190, undefined),
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (params) => {
        const date = dayjs(params);
        return date.isValid() ? date.format('DD/MM/YYYY h:mm:ss A') : '-';
      },
      showDateFilter: true,
      showTextFilter: false,
    },
    {
      field: EFFORT_UPLOAD.START_DATE,
      headerName: EFFORT_UPLOAD.EFFORT_START_DATE,
      flex: getColumnFlex(isMobileView, undefined, 0.2),
      width: getColumnWidth(isMobileView, 160, undefined),
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (params) => {
        if (params == null) {
          return '-';
        }
        const date = dayjs(params);
        return date.isValid() ? date.format('DD MMM, YYYY') : '-';
      },
      showDateFilter: true,
      showTextFilter: false,
    },
    {
      field: EFFORT_UPLOAD.END_DATE,
      headerName: EFFORT_UPLOAD.EFFORT_END_DATE,
      flex: getColumnFlex(isMobileView, undefined, 0.2),
      width: getColumnWidth(isMobileView, 160, undefined),
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (params) => {
        if (params == null) {
          return '-';
        }
        const date = dayjs(params);
        return date.isValid() ? date.format('DD MMM, YYYY') : '-';
      },
      showDateFilter: true,
      showTextFilter: false,
    },
    {
      field: EFFORT_UPLOAD.RUN_BY,
      headerName: FILE_UPLOAD.COLUMN_UPLOAD_BY,
      flex: getColumnFlex(isMobileView, undefined, 0.14),
      width: getColumnWidth(isMobileView, 110, undefined),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: EFFORT_UPLOAD.STATUS,
      headerName: EFFORT_UPLOAD.EFFORT_STATUS,
      flex: getColumnFlex(isMobileView, undefined, 0.15),
      width: getColumnWidth(isMobileView, 120, undefined),
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        let className = '';
        switch (params.value) {
        case 'Completed':
          className = 'status-completed';
          break;
        case 'In progress':
          className = 'status-in-progress';
          break;
        case 'Failed':
          className = 'status-failed';
          break;
        default:
        }
        return <span className={className}>{params?.value}</span>;
      },
    },
  ];

  const sortFilterColumns = [
    columns[0].field,
    columns[1].field,
    columns[2].field,
    columns[3].field,
    columns[4].field,
    columns[5].field,
  ];

  return (
    <div className="effort-upload-outer-layer">
      <div className="effort-upload">
        <div className="effort-upload-title">
          {LEAVE_UPLOAD.FILE_UPLOAD}
        </div>
        <div className="effort-search-and-buttons">
          <div className="effort-search-dropdown">
          </div>

          <div className="effort-button">
            <div className="run-effort-uplaod-btn">
              <ButtonComponent
                disabled={!checkAccess('create')}
                label={LEAVE_UPLOAD.FILE_UPLOAD}
                variant="contained"
                onClick={() => {
                  setAddModalOpen(true);
                  setIsEdit(false);
                }}
                btnCss={
                  !checkAccess('create')
                    ? 'run-effort-btn-disabled'
                    : 'run-effort-btn'
                }
              ></ButtonComponent>
            </div>
            <FileUploadPopUp
              openAddModal={addModalOpen}
              handleClose={() => {
                setAddModalOpen(false);
              }}
              runBy={userProfile?.user_name}
              onUploadComplete={handleUploadComplete}
            />
          </div>
        </div>
        <div className="data-grid-container">
          {filteredRows.length > 0 ? (

            <DataGridComponent
              dataRows={filteredRows}
              dataColumns={columns}
              sortFilterColumns={sortFilterColumns}
              pageSize={10}
              isFilteredRows={filteredRows}
              isEdit={isEdit}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
